import trim from 'lodash/trim'
import { defineMessages } from 'react-intl'

import { AddressManager } from '@app/utils/AddressManager'
import { assertNever } from '@app/utils/assertNever'
import { createSelector } from '@app/utils/createSelector'
import { isValidEmail } from '@app/utils/isValidEmail'
import { decodeJwtPayload } from '@app/utils/jwt'
import { DEFAULT_CURRENCY } from '@app/utils/printCurrency'

import { asNetworkError } from '@app/packages/api/errors'
import { asError } from '@app/packages/asError/asError'
import { createPromise } from '@app/packages/thunkSelector/promiseState'
import type { ThunkSelector } from '@app/packages/thunkSelector/thunkSelector'
import { createLoadable, mapThunkSelector } from '@app/packages/thunkSelector/thunkSelector'
import { withTransaction } from '@app/packages/thunkTransaction/thunkTransaction'

import { clearInvalidSession, refreshSessionCookie } from '@app/store/actions/session'
import { createReduxSlice } from '@app/store/redux_slice'
import { guestChatSessionSelector } from '@app/store/slices/guest_chat_session'
import { intlSelector } from '@app/store/slices/intl'
import { profileUserSelector } from '@app/store/slices/profile'
import { profileHasAvatarsSelector } from '@app/store/slices/profile_avatars'
import { authIdentitySelector, effectiveAccessTokenSelector } from '@app/store/slices/session'

export const profileTimezoneOffsetSelector = createSelector([profileUserSelector], profile => {
  if (profile) return profile.utc_offset / 60
  if (IS_BROWSER) {
    return new Date().getTimezoneOffset() * -1
  }

  return 0
})

export const profileUserTimezoneStringSelector = createSelector([profileUserSelector], profile => {
  return profile?.timezone
})

export const profilePhoneSelector = createSelector([profileUserSelector], user => {
  let phone: string | null = user && user.phone ? user.phone : null
  if (phone && phone.length > 0 && phone.indexOf('+') !== 0) {
    phone = `+${phone}`
  }

  return phone
})

export const profileUserAccountTypeSelector = createSelector(
  [profileUserSelector],
  user => user && user.account_type && (user.account_type.toLowerCase() as 'parent' | 'visitor' | 'sitter')
)
export const isProfileUserSignedInSelector = createSelector(
  [profileUserSelector],
  user => !!(user && (user.account_type === 'parent' || (user.account_type === 'sitter' && (user.approved || user.training_completed))))
)

export const profileUserIdSelector = createSelector([profileUserSelector], user => user?.id ?? null)
export const profileUserTokenSelector = createSelector([profileUserSelector], user => (user?.account_type === 'sitter' ? user.token : null))

/**
 * The current user's chat_server identity from the JWT `chat_uid` claim — the external_id
 * chat_server carries, not the Rails `user.id`. A support-chat guest has one of its own;
 * returning null there would make `isSelfParticipant` treat every channel as ours.
 */
export const profileChatUidSelector = createSelector([effectiveAccessTokenSelector, profileUserSelector, guestChatSessionSelector], (token, user, guest) => {
  if (!user || user.account_type === 'visitor') return guest ? chatUidClaim(guest.accessToken) : null
  return chatUidClaim(token)
})

const profileUserMetaSlice = createReduxSlice<{ hash: string }>('profile_user')
export const profileUserSyncSlice = createReduxSlice<{ hash: string }>('profile_user_sync')

export const profileUserResultSelector = createSelector(
  [profileUserMetaSlice.selector, profileUserSyncSlice.selector, authIdentitySelector, profileUserSelector],
  (meta, sync, identity, user) =>
    createLoadable(c => {
      if (!identity) return null
      const hash = [identity, sync?.hash ?? ''].join(':')
      if (!meta || meta.hash !== hash)
        return c.load(
          createPromise('User must be fetched', hash, async dispatch => {
            const { readProfileUser } = await import('@app/store/actions/profile')
            const userTransaction = await dispatch(withTransaction(readProfileUser())).catch((e: unknown) => {
              // A rejected transaction commits nothing, so the stale profile stays as it was.
              if (asNetworkError(asError(e))?.status !== 401) throw e
              dispatch(clearInvalidSession())
              dispatch(profileUserMetaSlice.set({ hash }))
              return null
            })
            if (!userTransaction) return

            userTransaction.commit()
            await dispatch(refreshSessionCookie())
            dispatch(profileUserMetaSlice.set({ hash }))
          })
        )
      return user
    })
)

export const profileUserAccountTypeResultSelector = mapThunkSelector(profileUserResultSelector, user => user?.account_type)

export const registrationCompleteSelector = createSelector(
  [profileUserSelector],
  user => !!(user && user.account_type !== 'visitor' && user.registration_completed)
)

export const profileIsParentSelector = createSelector([profileUserAccountTypeSelector], accountType => accountType === 'parent')
export const profileIsSitterSelector = createSelector([profileUserAccountTypeSelector], accountType => accountType === 'sitter')
export const profileIsVisitorSelector = createSelector([profileUserAccountTypeSelector], accountType => accountType === 'visitor')

export const profileRegionIsChangeableSelector = createSelector(
  [profileUserSelector],
  user => !(user && (('region_id' in user && user.region_id) || ('place_id' in user && user.place_id)))
)

export const profileCurrencySelector = createSelector([profileUserSelector], profile => {
  if (profile?.account_type === 'sitter') return profile.rate_currency
  if (profile?.account_type === 'parent') return profile.display_currency
  return DEFAULT_CURRENCY
})

export const profileSitterAddressSelector = createSelector([profileUserSelector], user => {
  if (user?.account_type !== 'sitter') return undefined
  if (!user.location) return undefined
  return AddressManager.fromLocation(user.location).address
})

export type ProfileCheckUser =
  | {
      account_type: 'sitter'
      first_name: string
      last_name: string
      birthday: string
      email: string
      timezone: string
      location: unknown
      education_and_experience: string
      late_booking_transport_details: string
      late_arrival_policy: string
      hasAvatars: boolean
      max_children: number
      work_online: boolean
      rate: string
      rate_for_2_kids: string
      rate_for_3_kids: string
      rate_for_4_kids: string
      rate_for_5_kids: string
      rate_online: string
      animals: string
    }
  | { account_type: 'parent'; first_name: string; last_name: string; email: string; place: unknown; timezone: string }

export const createProfileCompletionErrorsResultSelector = (profileSelector: ThunkSelector<ProfileCheckUser | null>) =>
  createSelector([profileSelector, intlSelector], (profileResult, { formatMessage }) =>
    createLoadable(c => {
      const profile = c.unwrap(profileResult)
      if (!profile) return null
      const errors: string[] = []
      if (profile.account_type === 'sitter') {
        if (!isValidString(profile.first_name)) errors.push(formatMessage(completionErrors.name))
        if (!isValidString(profile.last_name)) errors.push(formatMessage(completionErrors.lastname))
        if (!profile.birthday || profile.birthday.length !== 10) errors.push(formatMessage(completionErrors.birthday))
        if (!isValidEmail(profile.email)) errors.push(formatMessage(completionErrors.email))
        if (!profile.timezone) errors.push(formatMessage(completionErrors.address))
        if (!profile.location) errors.push(formatMessage(completionErrors.address))

        const about = profile.education_and_experience?.trim()
        if (!isValidString(about)) errors.push(formatMessage(completionErrors.education_and_experience))

        if (!profile.hasAvatars) errors.push(formatMessage(completionErrors.avatar))
        if (profile.max_children >= 1 && !isValidRate(profile.rate)) errors.push(formatMessage(completionErrors.rate))
        if (profile.max_children >= 2 && !isValidRate(profile.rate_for_2_kids)) errors.push(formatMessage(completionErrors.rate_2))
        if (profile.max_children >= 3 && !isValidRate(profile.rate_for_3_kids)) errors.push(formatMessage(completionErrors.rate_3))
        if (profile.max_children >= 4 && !isValidRate(profile.rate_for_4_kids)) errors.push(formatMessage(completionErrors.rate_4))
        if (profile.max_children >= 5 && !isValidRate(profile.rate_for_5_kids)) errors.push(formatMessage(completionErrors.rate_5))
        if (profile.work_online && !isValidRate(profile.rate_online)) errors.push(formatMessage(completionErrors.rate_online))
        if (!isValidString(profile.animals)) errors.push(formatMessage(completionErrors.animals))
      } else if (profile.account_type === 'parent') {
        if (!profile.first_name) errors.push(formatMessage(completionErrors.name))
        if (!profile.last_name) errors.push(formatMessage(completionErrors.lastname))
        if (!profile.email) errors.push(formatMessage(completionErrors.email))
        if (!profile.place) errors.push(formatMessage(completionErrors.region))
        if (!profile.timezone) errors.push(formatMessage(completionErrors.timezone))
      } else {
        assertNever(profile)
      }
      if (!errors.length) return null
      return errors
    })
  )

const profileCheckUserResultSelector = createSelector([profileUserResultSelector, profileHasAvatarsSelector], (profileResult, hasAvatars) =>
  createLoadable<ProfileCheckUser | null>(c => {
    const user = c.unwrap(profileResult)
    if (!user) return null
    switch (user.account_type) {
      case 'parent': {
        return {
          account_type: 'parent',
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email ?? '',
          place: String(user.place_id),
          timezone: user.timezone,
        }
      }
      case 'sitter': {
        return {
          account_type: 'sitter',
          first_name: user.first_name,
          last_name: user.last_name,
          birthday: user.birthday,
          email: user.email,
          timezone: user.timezone,
          location: user.location,
          education_and_experience: user.education_and_experience ?? '',
          late_booking_transport_details: user.late_booking_transport_details ?? '',
          late_arrival_policy: user.late_arrival_policy ?? '',
          hasAvatars,
          max_children: user.max_children,
          work_online: user.work_online,
          rate: String(user.rate),
          rate_for_2_kids: String(user.rate_for_2_kids),
          rate_for_3_kids: String(user.rate_for_3_kids),
          rate_for_4_kids: String(user.rate_for_4_kids),
          rate_for_5_kids: String(user.rate_for_5_kids),
          rate_online: String(user.rate_online),
          animals: user.animals,
        }
      }
      case 'visitor':
      default:
        return null
    }
  })
)

export const profileCompletionErrorsSelector = createProfileCompletionErrorsResultSelector(profileCheckUserResultSelector)
export const profileCompletedSelector = createSelector([profileCompletionErrorsSelector], list => !list)

function chatUidClaim(token: string | null): string | null {
  const uid = token ? decodeJwtPayload(token)?.chat_uid : null
  return typeof uid === 'string' && uid !== '' ? uid : null
}

function isValidString(val: string) {
  const trimmedString = trim(val || '', ' \n')
  return !!trimmedString && trimmedString.length > 0
}

function isValidRate(string: string) {
  const val = parseFloat(string)
  return !!val && val > 0
}

const completionErrors = defineMessages({
  name: { id: "store.selectors.profile.completionErrors.name.ba62", defaultMessage: 'Укажите имя' },
  lastname: { id: "store.selectors.profile.completionErrors.lastname.ab3a", defaultMessage: 'Укажите фамилию' },
  birthday: { id: "store.selectors.profile.completionErrors.birthday.f789", defaultMessage: 'Укажите дату рождения' },
  region: { id: "store.selectors.profile.completionErrors.region.d6e6", defaultMessage: 'Укажите регион' },
  email: { id: "store.selectors.profile.completionErrors.email.d166", defaultMessage: 'Укажите Email' },
  address: { id: "store.selectors.profile.completionErrors.address.6119", defaultMessage: 'Укажите адрес' },
  education_and_experience: { id: "store.selectors.profile.completionErrors.education_and_experience.0773", defaultMessage: 'Напишите о своем опыте и образовании' },
  avatar: { id: "store.selectors.profile.completionErrors.avatar.e095", defaultMessage: 'Добавьте аватар' },
  rate: { id: "store.selectors.profile.completionErrors.rate.4fec", defaultMessage: 'Укажите оплату за час' },
  rate_2: { id: "store.selectors.profile.completionErrors.rate_2.0184", defaultMessage: 'Укажите оплату за двух детей' },
  rate_3: { id: "store.selectors.profile.completionErrors.rate_3.79ec", defaultMessage: 'Укажите оплату за трех детей' },
  rate_4: { id: "store.selectors.profile.completionErrors.rate_4.a48a", defaultMessage: 'Укажите оплату за 4-х детей' },
  rate_5: { id: "store.selectors.profile.completionErrors.rate_5.fd14", defaultMessage: 'Укажите оплату за 5-x детей' },
  rate_online: { id: "store.selectors.profile.completionErrors.rate_online.a93d", defaultMessage: 'Укажите оплату за онлайн' },
  animals: { id: "store.selectors.profile.completionErrors.animals.62d0", defaultMessage: 'Опишите отношение к животным' },
  timezone: { id: "store.selectors.profile.completionErrors.timezone.1ca5", defaultMessage: 'Укажите часовой пояс' },
})
